import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatRupiah, formatDateID, formatAkuntansi as fA } from "@/lib/format";
import { PLStatement, BSStatement } from "./ReportStatements";
import { ReportPeriodFilter, defaultPeriod, periodRange, periodLabels } from "./ReportPeriodFilter";
import { BookOpen, ClipboardList, FileBarChart2, Scale, Wallet, Play, ShieldCheck, ArrowRightLeft } from "lucide-react";

function DashCard({ label, value, accent = "slate" }) {
  const bg = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    slate: "bg-slate-50 border-slate-200 text-slate-800" }[accent];
  return <div className={`border rounded-md p-5 ${bg}`}><div className="text-xs opacity-80">{label}</div><div className="font-mono-num text-2xl font-semibold mt-1">{value}</div></div>;
}

export function AccountingHub() {
  const navigate = useNavigate();
  const [d, setD] = useState({});
  useEffect(() => { api.get("/acc/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  const postAll = async () => {
    if (!window.confirm("Posting seluruh transaksi SIAP DIPOSTING ke Buku Besar?")) return;
    try { const { data } = await api.post("/acc/post-all"); toast.success(`Posting selesai: ${data.success} jurnal, ${data.failed.length} gagal.`); const { data: dd } = await api.get("/acc/dashboard"); setD(dd); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="accounting-hub">
      <div className="flex items-end justify-between">
        <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Akuntansi</h1><p className="text-slate-500 mt-2">Mesin jurnal, buku besar, dan laporan keuangan.</p></div>
        <Button onClick={postAll} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="post-all-btn"><Play className="h-4 w-4 mr-1.5" /> Posting Semua Siap Diposting</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label="Transaksi Siap Diposting" value={d.ready_to_post ?? "—"} accent="amber" />
        <DashCard label="Posting Gagal" value={d.failed ?? "—"} accent="rose" />
        <DashCard label="Jurnal Draf" value={d.draft_journals ?? "—"} accent="slate" />
        <DashCard label="Jurnal Diposting" value={d.posted_journals ?? "—"} accent="emerald" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          ["/akuntansi/jurnal", "Jurnal Umum", "Daftar semua jurnal & pembalik.", ClipboardList],
          ["/akuntansi/buku-besar", "Buku Besar", "Rincian per akun.", BookOpen],
          ["/akuntansi/neraca-saldo", "Neraca Saldo", "Total debit vs kredit.", Scale],
          ["/laporan", "Pusat Laporan", "P&L, Neraca, Arus Kas, Ekuitas.", FileBarChart2],
          ["/akuntansi/integritas", "Pusat Integritas", "AR/AP/Bank Subledger vs GL.", ShieldCheck],
          ["/akuntansi/migrasi-saldo-awal", "Migrasi Saldo Awal", "Jurnal pembukaan & rekonsiliasi.", ArrowRightLeft],
          ["/akuntansi/aset", "Aset Tetap", "Kelola aset & penyusutan.", Wallet],
        ].map(([p, l, d, I]) => (
          <button key={p} onClick={() => navigate(p)} className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-500">
            <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center mb-3"><I className="h-5 w-5" /></div>
            <div className="font-display font-semibold">{l}</div><div className="text-xs text-slate-500 mt-1">{d}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function TrialBalancePage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/acc/trial-balance").then(({ data }) => setData(data)); }, []);
  if (!data) return <div>Memuat data...</div>;
  return (
    <div className="space-y-6 page-fade" data-testid="trial-balance-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Neraca Saldo</h1>
        <p className="text-slate-500 mt-2">Saldo semua akun dari jurnal yang telah diposting.</p></div>
      <div className={`inline-flex px-3 py-1.5 rounded-md text-sm font-medium ${data.balanced ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
        {data.balanced ? "✓ SEIMBANG" : "⚠ TIDAK SEIMBANG"} — Debit {formatRupiah(data.total_debit)} · Kredit {formatRupiah(data.total_credit)}
      </div>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama Akun</TableHead><TableHead>Kategori</TableHead><TableHead className="text-right">Total Debit</TableHead><TableHead className="text-right">Total Kredit</TableHead><TableHead className="text-right">Saldo Debit</TableHead><TableHead className="text-right">Saldo Kredit</TableHead></TableRow></TableHeader>
          <TableBody>{data.rows.map((r) => (
            <TableRow key={r.account_code}><TableCell className="font-mono-num">{r.account_code}</TableCell><TableCell>{r.account_name}</TableCell><TableCell><Badge variant="outline">{r.category}</Badge></TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.total_debit)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.total_credit)}</TableCell><TableCell className="text-right font-mono-num font-semibold">{formatRupiah(r.balance_debit)}</TableCell><TableCell className="text-right font-mono-num font-semibold">{formatRupiah(r.balance_credit)}</TableCell></TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

export function GeneralLedgerPage() {
  const [account, setAccount] = useState("");
  const [coa, setCoa] = useState([]);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/masters/chart_of_accounts").then(({ data }) => setCoa(data.filter((c) => c.status === "ACTIVE" && c.allow_direct_posting))); }, []);
  const load = () => {
    if (!account) return;
    api.get("/acc/general-ledger", { params: { account_code: account } }).then(({ data }) => setData(data));
  };
  return (
    <div className="space-y-6 page-fade" data-testid="general-ledger-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Buku Besar</h1><p className="text-slate-500 mt-2">Rincian transaksi per akun.</p></div>
      <Card><CardContent className="p-4">
        <div className="flex gap-2 mb-4">
          <select value={account} onChange={(e) => setAccount(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm min-w-64">
            <option value="">-- Pilih Akun --</option>
            {coa.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
          </select>
          <Button onClick={load} disabled={!account}>Muat</Button>
        </div>
        {data && <>
          <div className="text-sm text-slate-600 mb-2">Total Debit: <b>{formatRupiah(data.total_debit)}</b> · Total Kredit: <b>{formatRupiah(data.total_credit)}</b></div>
          <Table><TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jurnal</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
            <TableBody>{data.lines.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Belum ada mutasi.</TableCell></TableRow> : data.lines.map((l) => (
              <TableRow key={l.id}><TableCell>{formatDateID(l.date)}</TableCell><TableCell className="font-mono-num">{l.journal_number}</TableCell><TableCell>{l.description}</TableCell><TableCell className="text-right font-mono-num">{l.debit ? formatRupiah(l.debit) : ""}</TableCell><TableCell className="text-right font-mono-num">{l.credit ? formatRupiah(l.credit) : ""}</TableCell><TableCell className="text-right font-mono-num font-semibold">{formatRupiah(l.running_balance)}</TableCell></TableRow>
            ))}</TableBody></Table>
        </>}
      </CardContent></Card>
    </div>
  );
}

export function JournalListPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/acc/journals", { params: { limit: 500 } }).then(({ data }) => setRows(data)); }, []);
  return (
    <div className="space-y-6 page-fade" data-testid="journal-list-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Jurnal Umum</h1><p className="text-slate-500 mt-2">Seluruh jurnal (otomatis, manual, dan pembalik).</p></div>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Belum ada jurnal.</TableCell></TableRow> : rows.map((j) => (
            <TableRow key={j.id}><TableCell className="font-mono-num">{j.number}</TableCell><TableCell>{formatDateID(j.date)}</TableCell><TableCell className="text-xs">{j.journal_type}</TableCell><TableCell className="text-sm">{j.description}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(j.total_debit)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(j.total_credit)}</TableCell><TableCell><Badge className={j.status === "DIPOSTING" ? "bg-emerald-600" : j.status === "DIBALIK" ? "bg-amber-500" : "bg-slate-500"}>{j.status}</Badge></TableCell></TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

export function ReportsHub() {
  const [pl, setPl] = useState(null);
  const [bs, setBs] = useState(null);
  const [cf, setCf] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod);
  const [years, setYears] = useState([]);
  const [span, setSpan] = useState({});
  useEffect(() => { api.get("/acc/reports/periods").then(({ data }) => { setYears(data.years); setSpan(data); }).catch(() => {}); }, []);
  useEffect(() => {
    if (years.length && !years.includes(period.year)) setPeriod((p) => ({ ...p, year: years[0] }));
  }, [years, period.year]);
  useEffect(() => {
    const r = periodRange(period);
    setPl(null); setBs(null); setCf(null);
    let live = true;
    const fail = (e) => { if (live) toast.error(formatApiError(e)); };
    const ok = (set) => ({ data }) => { if (live) set(data); };
    api.get("/acc/reports/profit-loss", { params: r }).then(ok(setPl)).catch(fail);
    api.get("/acc/reports/balance-sheet", { params: r }).then(ok(setBs)).catch(fail);
    api.get("/acc/reports/cash-flow", { params: r }).then(ok(setCf)).catch(fail);
    return () => { live = false; };
  }, [period]);
  const lbl = periodLabels(period, span);
  const sub = (t, id) => <div className="text-xs text-slate-500 font-normal mt-1" data-testid={id}>{t}</div>;
  return (
    <div className="space-y-6 page-fade" data-testid="reports-hub">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Laporan</h1><p className="text-slate-500 mt-2">Laporan keuangan berbasis Buku Besar.</p></div>
      <ReportPeriodFilter value={period} onChange={setPeriod} years={years} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <Card data-testid="report-pl-card"><CardHeader><CardTitle className="font-display">Laporan Laba Rugi{sub(lbl.flow, "report-pl-period")}</CardTitle></CardHeader><CardContent>
          {!pl ? <div className="text-slate-500">Memuat...</div> : <PLStatement pl={pl} />}
        </CardContent></Card>
        <Card data-testid="report-bs-card"><CardHeader><CardTitle className="font-display">Neraca{sub(lbl.asOf, "report-bs-period")}</CardTitle></CardHeader><CardContent>
          {!bs ? <div className="text-slate-500">Memuat...</div> : <BSStatement bs={bs} />}
        </CardContent></Card>
        <Card className="xl:col-span-2" data-testid="report-cf-card"><CardHeader><CardTitle className="font-display">Laporan Arus Kas (Metode Tidak Langsung){sub(lbl.flow, "report-cf-period")}</CardTitle></CardHeader><CardContent>
          {!cf ? <div className="text-slate-500">Memuat...</div> : (
            <table className="w-full text-sm"><tbody>
              <tr><td className="py-1.5">Saldo Kas Awal</td><td className="text-right font-mono-num" data-testid="report-cf-opening">{fA(cf.opening)}</td></tr>
              <tr><td className="py-1.5">Aktivitas Operasi</td><td className="text-right font-mono-num">{fA(cf.operating)}</td></tr>
              <tr><td className="py-1.5">Aktivitas Investasi</td><td className="text-right font-mono-num">{fA(cf.investing)}</td></tr>
              <tr><td className="py-1.5">Aktivitas Pendanaan</td><td className="text-right font-mono-num">{fA(cf.financing)}</td></tr>
              <tr className="border-t border-slate-200"><td className="py-1.5">Perubahan Kas Bersih</td><td className="text-right font-mono-num font-semibold">{fA(cf.net_change)}</td></tr>
              <tr className="border-t-2 border-slate-900 font-bold"><td className="py-2">Saldo Kas Akhir</td><td className="text-right font-mono-num" data-testid="report-cf-closing">{fA(cf.closing)}</td></tr>
            </tbody></table>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}

export function IntegrityPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/acc/integrity").then(({ data }) => setD(data)); }, []);
  if (!d) return <div>Memuat...</div>;
  const check = (obj, label, keyLeft, keyRight) => (
    <tr className={obj.status === "SESUAI" ? "" : "bg-rose-50"}><td className="py-2 px-3">{label}</td><td className="text-right font-mono-num">{formatRupiah(obj[keyLeft])}</td><td className="text-right font-mono-num">{formatRupiah(obj[keyRight])}</td><td className="text-right font-mono-num">{formatRupiah(obj.diff || obj.difference || 0)}</td><td><Badge className={obj.status === "SESUAI" ? "bg-emerald-600" : "bg-rose-600"}>{obj.status}</Badge></td></tr>
  );
  return (
    <div className="space-y-6 page-fade" data-testid="integrity-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Integritas Akuntansi</h1><p className="text-slate-500 mt-2">Validasi kesetaraan subledger vs Buku Besar.</p></div>
      <Card><CardContent className="p-4">
        <table className="w-full text-sm">
          <thead><tr className="text-left"><th className="py-2 px-3">Cek</th><th className="text-right">Nilai 1</th><th className="text-right">Nilai 2</th><th className="text-right">Selisih</th><th>Status</th></tr></thead>
          <tbody>
            {check(d.trial_balance, "Neraca Saldo (Debit vs Kredit)", "debit", "credit")}
            {check(d.balance_sheet, "Neraca (Aset vs Liab+Ekuitas)", "assets", "liab_equity")}
            {check(d.ar, "Piutang (Subledger vs GL)", "subledger", "gl")}
            {check(d.ap, "Hutang (Subledger vs GL)", "subledger", "gl")}
            {check(d.bank, "Bank (Operasional vs GL)", "subledger", "gl")}
            {d.cash && check(d.cash, "Kas (Operasional vs GL)", "subledger", "gl")}
            {d.asset && check(d.asset, "Aset Tetap (Register vs GL)", "subledger", "gl")}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

export function AssetsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/acc/assets").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="space-y-6 page-fade" data-testid="assets-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Aset Tetap</h1><p className="text-slate-500 mt-2">Kelola aset dan jadwal penyusutan (metode Garis Lurus).</p></div>
      <Card><CardContent className="p-4">
        <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Kategori</TableHead><TableHead className="text-right">Harga Perolehan</TableHead><TableHead className="text-right">Penyusutan/bln</TableHead><TableHead className="text-right">Nilai Buku</TableHead></TableRow></TableHeader>
          <TableBody>{items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada aset. Fondasi database aset tetap tersedia; pendaftaran dapat dilakukan via API.</TableCell></TableRow> : items.map((a) => (
            <TableRow key={a.id}><TableCell className="font-mono-num">{a.code}</TableCell><TableCell>{a.name}</TableCell><TableCell>{a.category}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(a.acquisition_cost)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(a.monthly_depreciation)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(a.current_book_value)}</TableCell></TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}
